/**
 * Voyado Elevate product recommendations for PDP.
 * Depends on elevate-core.js
 */

class ElevateProductRecommendations extends HTMLElement {
  #loaded = false;
  #config = null;
  #tickets = [];

  connectedCallback() {
    this.loadRecommendations();
  }

  async loadRecommendations() {
    if (this.#loaded) {
      return;
    }

    this.#loaded = true;

    const { ElevateCore } = window;

    if (!ElevateCore?.readConfigFromElement) {
      this.remove();
      return;
    }

    this.#config = {
      ...ElevateCore.readConfigFromElement(this),
      productKey: this.dataset.productKey,
      recommendationListId: this.dataset.recommendationListId,
      limit: parseInt(this.dataset.limit || '4', 10)
    };

    if (!this.#config?.clusterId || !this.#config.productKey || !this.#config.recommendationListId) {
      this.remove();
      return;
    }

    try {
      const fetchLimit = Math.max(this.#config.limit * 3, 12);
      const data = await ElevateCore.fetchProductPage(this.#config, {
        recommendationLists: [
          {
            id: this.#config.recommendationListId,
            limit: fetchLimit
          }
        ]
      });
      const recList =
        data.recommendationLists?.find((list) => list.id === this.#config.recommendationListId) ??
        data.recommendationLists?.[0];

      if (!recList?.visible || !recList.productGroups?.length) {
        this.remove();
        return;
      }

      const candidates = [];

      for (const group of recList.productGroups) {
        const mainProduct = group.products?.[0];

        if (!mainProduct) {
          continue;
        }

        const shopifyId = ElevateCore.parseShopifyProductId(mainProduct.key);
        const handle = (mainProduct.link || '').split('/products/').pop()?.split('?')[0];

        if (!shopifyId || !handle) {
          continue;
        }

        candidates.push({
          shopifyId,
          handle,
          ticket: mainProduct.ticket ?? ''
        });
      }

      const live = (
        await Promise.all(
          candidates.map(async (candidate) => {
            try {
              const response = await fetch(`${Shopify.routes.root}products/${encodeURIComponent(candidate.handle)}.js`);
              return response.ok ? candidate : null;
            } catch (_error) {
              return null;
            }
          })
        )
      )
        .filter(Boolean)
        .slice(0, this.#config.limit);

      this.#tickets = live.map((item) => item.ticket);
      const productIds = live.map((item) => item.shopifyId);

      if (!productIds.length) {
        this.remove();
        return;
      }

      const sectionId = ElevateCore.extractSectionId(this);
      const sectionNode = await ElevateCore.renderProductsSection(this.#config, productIds, sectionId);
      const recommendationsElement = sectionNode?.querySelector('elevate-product-recommendations');

      if (!recommendationsElement?.childElementCount) {
        this.remove();
        return;
      }

      this.replaceChildren(...document.importNode(recommendationsElement, true).childNodes);
      this.hidden = false;

      if (recList.label) {
        const heading = this.querySelector('.section-header h2, .section-header h3, [class*="section-header"] h2, [class*="section-header"] h3');

        if (heading) {
          heading.textContent = recList.label;
        }
      }

      ElevateCore.attachTickets(this, this.#tickets, productIds);
      ElevateCore.bindClickTracking(this, this.#config);
    } catch (_error) {
      this.remove();
    }
  }
}

if (!window.customElements.get('elevate-product-recommendations')) {
  window.customElements.define('elevate-product-recommendations', ElevateProductRecommendations);
}
